/**
 * useCalendarConnections / useUpcomingCalendarEvents — Track C calendar hub.
 *
 * v1 reference: ``ando-frontend/app/pages/CalendarHubPage.tsx`` lines
 * 140-214 (manual ``loadConnections`` + per-connection ``fetchUpcomingEvents``
 * loop, polled every 5 min via ``useConnectionHealthPolling``).
 *
 * v2 replaces the bespoke polling with TanStack Query ``refetchInterval`` —
 * parity v1 is "polling, not WS", and the chat WS bridge is reserved for
 * conversation events only.
 *
 * Capability gate: BE returns 403 for ``/calendar/*`` when the user has no
 * calendar capability. ``useHasCapability("calendar_oauth")`` short-circuits
 * the query so we don't hammer 403s in dev.
 */

import {
  type CalendarConnection,
  type CalendarEvent,
  type CalendarEventsResponse,
  getCalendarConnections,
  getCalendarEvents,
} from "@ando/core";
import { type UseQueryResult, queryOptions, useQueries, useQuery } from "@tanstack/react-query";

import { useHasCapability } from "@/features/capabilities";
import { useChatUserId } from "@/features/chat";

export const calendarConnectionsQueryKey = ["workspace", "calendar", "connections"] as const;

/** 5 min — mirrors v1's ``useConnectionHealthPolling`` token-health cadence. */
const CALENDAR_CONNECTIONS_STALE_TIME_MS = 5 * 60_000;

/**
 * Shared query definition for the calendar-connections list. Consumed by BOTH
 * the ``useCalendarConnections`` hook and the ``/workspace/calendar`` route
 * loader (``ensureQueryData``) so hover/focus intent-preload warms the SAME
 * cache entry the hub reads — the connections list (usually the slow first
 * fetch) paints from cache instead of spinning.
 *
 * The key is a pure constant (no userId / capability in it), so the loader can
 * preload unconditionally; the ``"calendar_oauth"`` capability gate stays on
 * the HOOK's ``enabled`` flag (a failed prefetch is fire-and-forget). The
 * events fan-out (``useUpcomingCalendarEvents``) depends on the fetched
 * connections at runtime and is therefore intentionally NOT preloadable.
 */
export function calendarConnectionsQueryOptions() {
  return queryOptions({
    queryKey: calendarConnectionsQueryKey,
    queryFn: () => getCalendarConnections(),
    staleTime: CALENDAR_CONNECTIONS_STALE_TIME_MS,
  });
}

export interface UseCalendarConnectionsResult {
  readonly connections: readonly CalendarConnection[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  /** ``false`` when ``"calendar_oauth"`` capability isn't granted. */
  readonly hasAccess: boolean;
  readonly refetch: () => Promise<unknown>;
}

/** GET ``/calendar/connections`` — polled every 5 min to mirror v1 health check. */
export function useCalendarConnections(): UseCalendarConnectionsResult {
  // The BE-side capability slot is named ``calendar_oauth`` (matches the
  // manifest entry for the connector wizard); fall back to ``"calendar"``
  // for older manifests that haven't been migrated yet.
  const hasViaOauth = useHasCapability("calendar_oauth");
  const hasViaCalendar = useHasCapability("calendar");
  const hasAccess = hasViaOauth || hasViaCalendar;

  const query = useQuery({
    ...calendarConnectionsQueryOptions(),
    enabled: hasAccess,
    // 5 min staleTime (from the factory) + refetchInterval mirrors v1's
    // ``useConnectionHealthPolling`` (token health check cadence).
    refetchInterval: 5 * 60_000,
  });

  return {
    connections: query.data?.connections ?? [],
    isLoading: hasAccess ? query.isLoading : false,
    isError: query.isError,
    error: query.error,
    hasAccess,
    refetch: () => query.refetch(),
  };
}

export interface UpcomingEventsBundle {
  readonly connection: CalendarConnection;
  readonly events: readonly CalendarEvent[];
  readonly isLoading: boolean;
}

export interface UseUpcomingCalendarEventsResult {
  readonly bundles: readonly UpcomingEventsBundle[];
  /** Flattened + sorted-by-start view for hub headers. */
  readonly all: readonly CalendarEvent[];
}

/**
 * Fan out to ``/calendar/events`` for every connected calendar in parallel,
 * returning a single sorted timeline for the hub header / hero copy.
 *
 * The ``[startIso, endIso)`` window is supplied by the caller (the calendar
 * hub derives it from the active view + navigated date via ``rangeBounds``),
 * so paging to any month / week fetches exactly that period's events. The
 * window is part of the query key → each period caches independently.
 *
 * v1 ref: same file (lines 150-207) — loop over connections + push results
 * into a Map. v2 uses ``useQueries`` so React Query handles each leg.
 */
export function useUpcomingCalendarEvents(
  connections: readonly CalendarConnection[],
  startIso: string,
  endIso: string,
): UseUpcomingCalendarEventsResult {
  const userId = useChatUserId();

  // Round down to the current 5-min slot so polling refreshes the viewed
  // window without churning the key on every render.
  const slot = Math.floor(Date.now() / (5 * 60_000));

  const results = useQueries({
    queries: connections.map((c) => ({
      queryKey: [
        "workspace",
        "calendar",
        "events",
        c.id,
        c.provider,
        c.email_address,
        startIso,
        endIso,
        slot,
      ] as const,
      queryFn: async (): Promise<CalendarEventsResponse> => {
        if (!userId) return { events: [] };
        return await getCalendarEvents({
          userId,
          provider: c.provider,
          emailAddress: c.email_address,
          start: startIso,
          end: endIso,
        });
      },
      enabled: userId !== null && !c.is_expired,
      staleTime: 60_000,
      refetchInterval: 5 * 60_000,
    })),
  }) as UseQueryResult<CalendarEventsResponse>[];

  const bundles: UpcomingEventsBundle[] = connections.map((connection, idx) => ({
    connection,
    events: results[idx]?.data?.events ?? [],
    isLoading: results[idx]?.isLoading ?? false,
  }));

  const all = bundles
    .flatMap((b) => b.events)
    .slice()
    .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));

  return { bundles, all };
}
